import { Card, CardContent } from '@/components/ui/card';
import { Link } from 'react-router-dom';
import { FileSpreadsheet, ClipboardList, BarChart3, Database, CheckCircle, AlertTriangle, BookOpen, Code } from 'lucide-react';

export default function AdministracaoParaDadosContent() {
  return (
    <div className="prose prose-lg max-w-none dark:prose-invert">
      <section className="not-prose mb-12">
        <div className="bg-gradient-to-b from-primary/5 to-background py-12 -mx-4 px-4 rounded-xl">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-6">
              <ClipboardList className="w-4 h-4" />
              <span className="text-sm font-semibold">Transição de Carreira</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-foreground">
              Administração para Dados: Excel Avançado como Porta de Entrada
            </h1>
            <p className="text-xl text-muted-foreground">
              Profissionais administrativos têm um ativo enorme e ignorado: domínio real de Excel.
              Veja como transformar isso em vaga de Analista de Dados em 2026.
            </p>
          </div>
        </div>
      </section>

      <section className="article-intro mb-12">
        <p className="text-lg leading-relaxed">
          <strong>Excel avançado é uma das porta de entrada mais legítimas para dados.</strong>
          Quem trabalha com administrativo, controladoria, BackOffice ou rotina de operações já
          modela planilhas complexas, faz validação cruzada e entrega relatório toda semana.
        </p>
        <p className="text-lg leading-relaxed">
          Segundo a <strong>Catho 2025</strong>, um analista administrativo pleno ganha entre
          R$ 3.500 e R$ 5.500. Um analista de dados júnior parte de R$ 4.500-6.500 e atinge
          R$ 8-12k em 18-24 meses como pleno. O salto está em trocar Excel manual por SQL +
          Power BI estruturado.
        </p>
      </section>

      <section className="not-prose mb-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card><CardContent className="pt-6 text-center">
            <div className="text-3xl font-bold text-primary mb-2">+50%</div>
            <p className="text-sm text-muted-foreground">Salto salarial em 18 meses</p>
          </CardContent></Card>
          <Card><CardContent className="pt-6 text-center">
            <div className="text-3xl font-bold text-primary mb-2">6-9 meses</div>
            <p className="text-sm text-muted-foreground">Tempo médio até a primeira vaga</p>
          </CardContent></Card>
          <Card><CardContent className="pt-6 text-center">
            <div className="text-3xl font-bold text-primary mb-2">3ª maior</div>
            <p className="text-sm text-muted-foreground">Origem de transições para dados</p>
          </CardContent></Card>
        </div>
        <p className="text-xs text-muted-foreground mt-3 text-center">Fonte: Catho 2025, State of Data Brazil 2024.</p>
      </section>

      <section className="mb-12">
        <h2 className="text-3xl font-bold mb-6 flex items-center gap-3 text-foreground">
          <CheckCircle className="w-8 h-8 text-primary" /> O Que Você Já Sabe
        </h2>
        <div className="not-prose grid md:grid-cols-2 gap-4">
          <Card><CardContent className="pt-6">
            <FileSpreadsheet className="w-6 h-6 text-primary mb-2" />
            <h3 className="font-semibold mb-2">Excel avançado de produção</h3>
            <p className="text-sm text-muted-foreground">PROCV/XLOOKUP, Tabela Dinâmica, ÍNDICE+CORRESP, fórmulas matriciais. Quem entrega planilha que vai para diretoria todo mês tem rigor de dado.</p>
          </CardContent></Card>
          <Card><CardContent className="pt-6">
            <ClipboardList className="w-6 h-6 text-primary mb-2" />
            <h3 className="font-semibold mb-2">Organização e processo</h3>
            <p className="text-sm text-muted-foreground">SOP, checklist, fluxograma. Documentar processo é exatamente o que falta na maioria dos times de dados.</p>
          </CardContent></Card>
          <Card><CardContent className="pt-6">
            <BarChart3 className="w-6 h-6 text-primary mb-2" />
            <h3 className="font-semibold mb-2">Relatório recorrente</h3>
            <p className="text-sm text-muted-foreground">Fechamento mensal, indicador semanal, atualização de KPI. Você já faz BI manual — falta só automatizar.</p>
          </CardContent></Card>
          <Card><CardContent className="pt-6">
            <Database className="w-6 h-6 text-primary mb-2" />
            <h3 className="font-semibold mb-2">Atenção a detalhe</h3>
            <p className="text-sm text-muted-foreground">Conferência, validação, reconciliação. Skill rara em quem vem de TI puro — empresas valorizam.</p>
          </CardContent></Card>
        </div>
      </section>

      <section className="mb-12">
        <h2 className="text-3xl font-bold mb-6 flex items-center gap-3 text-foreground">
          <Code className="w-8 h-8 text-primary" /> O Que Você Precisa Desenvolver
        </h2>
        <div className="not-prose space-y-4">
          <Card><CardContent className="pt-6">
            <h3 className="font-semibold mb-2">Power Query e Power Pivot</h3>
            <p className="text-sm text-muted-foreground">Ponte natural entre Excel e BI. Aprenda primeiro — está dentro do Excel que você já usa.</p>
          </CardContent></Card>
          <Card><CardContent className="pt-6">
            <h3 className="font-semibold mb-2">SQL básico e intermediário</h3>
            <p className="text-sm text-muted-foreground">SELECT, JOIN, GROUP BY. Substitui PROCV em base grande. Não precisa virar DBA.</p>
          </CardContent></Card>
          <Card><CardContent className="pt-6">
            <h3 className="font-semibold mb-2">Power BI</h3>
            <p className="text-sm text-muted-foreground">Modelagem em estrela, DAX e visualização. É o pulo do gato para sair de "analista de planilha" e virar "analista de dados".</p>
          </CardContent></Card>
          <Card><CardContent className="pt-6">
            <h3 className="font-semibold mb-2">Python para automação (opcional início)</h3>
            <p className="text-sm text-muted-foreground">Pandas substitui rotinas Excel pesadas. Entra como diferencial depois dos primeiros 6 meses.</p>
          </CardContent></Card>
        </div>
      </section>

      <section className="mb-12">
        <h2 className="text-3xl font-bold mb-6 text-foreground">Roadmap de 6-9 Meses</h2>
        <div className="not-prose space-y-4">
          <Card><CardContent className="pt-6">
            <div className="text-xs font-semibold text-primary mb-2">MÊS 1-2</div>
            <h3 className="font-semibold mb-2">Power Query + SQL básico</h3>
            <p className="text-sm text-muted-foreground">Aproveite o Excel como ponte. Aprenda Power Query e comece SQL em paralelo.</p>
          </CardContent></Card>
          <Card><CardContent className="pt-6">
            <div className="text-xs font-semibold text-primary mb-2">MÊS 3-5</div>
            <h3 className="font-semibold mb-2">Power BI completo</h3>
            <p className="text-sm text-muted-foreground">Modelagem, DAX, dashboards. Refaça relatórios do trabalho atual no Power BI.</p>
          </CardContent></Card>
          <Card><CardContent className="pt-6">
            <div className="text-xs font-semibold text-primary mb-2">MÊS 6-9</div>
            <h3 className="font-semibold mb-2">Portfólio e aplicações</h3>
            <p className="text-sm text-muted-foreground">3 dashboards públicos, currículo reposicionado, aplicações para Analista de Dados Júnior, BI Analyst e Analista de Operações Analytics.</p>
          </CardContent></Card>
        </div>
      </section>

      <section className="mb-12">
        <h2 className="text-3xl font-bold mb-6 text-foreground">Comparativo Salarial</h2>
        <div className="not-prose overflow-x-auto">
          <table className="w-full text-sm border">
            <thead className="bg-muted"><tr><th className="p-3 text-left">Cargo</th><th className="p-3 text-left">Salário Médio (BR)</th></tr></thead>
            <tbody>
              <tr className="border-t"><td className="p-3">Analista Administrativo Pleno</td><td className="p-3">R$ 3.500 – R$ 5.500</td></tr>
              <tr className="border-t"><td className="p-3">Analista de Dados Júnior</td><td className="p-3">R$ 4.500 – R$ 6.500</td></tr>
              <tr className="border-t"><td className="p-3">BI Analyst Pleno</td><td className="p-3">R$ 7.500 – R$ 12.000</td></tr>
              <tr className="border-t"><td className="p-3">Analista de Dados Sênior</td><td className="p-3">R$ 12.000 – R$ 18.000</td></tr>
            </tbody>
          </table>
          <p className="text-xs text-muted-foreground mt-2">Fonte: Catho 2025, Robert Half Salary Guide 2025.</p>
        </div>
      </section>

      <section className="mb-12">
        <h2 className="text-3xl font-bold mb-6 flex items-center gap-3 text-foreground">
          <AlertTriangle className="w-8 h-8 text-primary" /> Erros Comuns
        </h2>
        <ul className="space-y-3">
          <li>❌ Subestimar o próprio Excel — pular Power Query é desperdício de skill.</li>
          <li>❌ Estudar Python antes de Power BI.</li>
          <li>❌ Esconder experiência administrativa achando que "não conta".</li>
          <li>❌ Querer pular para Cientista de Dados sem passar por Analista.</li>
          <li>❌ Não construir portfólio público — só estudar não converte vaga.</li>
        </ul>
      </section>

      <section className="mb-12">
        <div className="not-prose space-y-2">
          <p className="text-sm text-muted-foreground">👉 Leia também: <Link to="/guias/migracao-excel-power-bi" className="text-primary hover:underline">Migração de Excel para Power BI</Link></p>
          <p className="text-sm text-muted-foreground">👉 Leia também: <Link to="/guias/transicao-carreira-dados" className="text-primary hover:underline">Como fazer transição para dados</Link></p>
          <p className="text-sm text-muted-foreground">👉 Leia também: <Link to="/guias/primeira-vaga-dados-2025" className="text-primary hover:underline">Primeira vaga em dados</Link></p>
        </div>
      </section>

      <section className="mb-12 faq-section">
        <h2 className="text-3xl font-bold mb-6 text-foreground">Perguntas Frequentes</h2>
        <div className="not-prose space-y-3">
          <details className="group border rounded-lg p-4">
            <summary className="font-semibold cursor-pointer">Excel avançado conta como experiência em dados no currículo?</summary>
            <p className="mt-3 text-sm text-muted-foreground">Sim, desde que descrito com resultado: "automatizei fechamento mensal de 15 lojas" pesa muito mais que "conhecimento avançado de Excel".</p>
          </details>
          <details className="group border rounded-lg p-4">
            <summary className="font-semibold cursor-pointer">Preciso saber Python para a primeira vaga?</summary>
            <p className="mt-3 text-sm text-muted-foreground">Não. SQL + Power BI bastam. Python entra como diferencial depois.</p>
          </details>
          <details className="group border rounded-lg p-4">
            <summary className="font-semibold cursor-pointer">Vagas de "BI" e "Analista de Dados" são a mesma coisa?</summary>
            <p className="mt-3 text-sm text-muted-foreground">Quase sempre sim no Brasil. BI Analyst tende a focar mais em Power BI/Tableau; Analista de Dados pede mais SQL e Python. Aplique para os dois.</p>
          </details>
          <details className="group border rounded-lg p-4">
            <summary className="font-semibold cursor-pointer">Posso migrar internamente sem trocar de empresa?</summary>
            <p className="mt-3 text-sm text-muted-foreground">É o caminho mais rápido. Comece automatizando relatórios do seu próprio time, mostre resultado e converse com gestor sobre transferência interna.</p>
          </details>
        </div>
      </section>

      <section className="not-prose mt-16">
        <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
          <CardContent className="pt-8 pb-8 text-center">
            <BookOpen className="w-12 h-12 text-primary mx-auto mb-4" />
            <h3 className="text-2xl font-bold mb-3">Transforme Excel em carreira de dados</h3>
            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">Comunidade ativa, vagas exclusivas e trilhas gratuitas em SQL, Power BI e Python.</p>
            <Link to="/register" className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-lg font-semibold hover:opacity-90 transition">Criar conta gratuita</Link>
          </CardContent>
        </Card>
        <p className="text-sm text-muted-foreground mt-6">Por <span className="font-medium text-foreground">Carolina Lima</span></p>
      </section>
    </div>
  );
}
